import { type ReactNode, useEffect, useId, useRef } from 'react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';

import { cardSx, chipSx, type ChipTone, wf } from '../shell/tokens';

const intakeJourneyStages = ['agreements', 'intake', 'verification', 'payers', 'import'] as const;
export type IntakeJourneyStage = (typeof intakeJourneyStages)[number];

const labels: Record<IntakeJourneyStage, string> = {
  agreements: 'Agreements & setup',
  intake: 'Intake form',
  verification: 'JotPsych verification',
  payers: 'Payer connections',
  import: 'Import patients',
};

/** Stages that keep their existing feature mounted when journey chrome is off. */
const liveStages: readonly IntakeJourneyStage[] = ['intake', 'payers'];

export interface IntakeJourneyStageState {
  statusLabel: string;
  /** One-line hint under the status, e.g. what the step covers. */
  caption?: string;
  /** Show the step as done. Only the owner of the stage's completion signal may set this. */
  completed?: boolean;
  /** Chip color. Defaults: completed green, locked grey, selected blue, otherwise grey. */
  tone?: ChipTone;
  lockedReason?: string;
}

/** Presentation only. The caller supplies authoritative status and navigation decisions. */
export interface IntakeJourneyShellProps {
  /** Remove journey chrome without remounting existing intake during a gate fallback. */
  showJourneyChrome?: boolean;
  selectedStage: IntakeJourneyStage;
  onNavigate: (stage: IntakeJourneyStage) => void;
  stages: Record<IntakeJourneyStage, IntakeJourneyStageState>;
  content: Record<IntakeJourneyStage, ReactNode>;
  toolbar?: ReactNode;
  kickoff?: ReactNode;
}

function StepMarker({
  index,
  completed,
  locked,
  selected,
}: {
  index: number;
  completed: boolean;
  locked: boolean;
  selected: boolean;
}) {
  const tone = completed
    ? { bgcolor: wf.green, color: wf.surface, borderColor: wf.green }
    : selected
      ? { bgcolor: wf.blue, color: wf.surface, borderColor: wf.blue }
      : locked
        ? { bgcolor: wf.surfaceAlt, color: wf.faint, borderColor: wf.line }
        : { bgcolor: wf.surface, color: wf.muted, borderColor: wf.line };
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        border: '1px solid',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 13,
        fontWeight: 700,
        ...tone,
      }}
    >
      {completed ? (
        <CheckOutlinedIcon sx={{ fontSize: 16 }} />
      ) : locked ? (
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
      ) : (
        index + 1
      )}
    </Box>
  );
}

/** Keep supplied content mounted across stage changes; the owner retains scope/save guards. */
export function IntakeJourneyShell({
  showJourneyChrome = true,
  selectedStage,
  onNavigate,
  stages,
  content,
  toolbar,
  kickoff,
}: IntakeJourneyShellProps) {
  const id = useId();
  const navRef = useRef<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // On desktop the stepper sticks to the page scrollport; the desktop intake
  // rail reads its height so the two sticky navigation areas stack correctly.
  useEffect(() => {
    const nav = navRef.current;
    const root = rootRef.current;
    if (!nav || !root || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      root.style.setProperty('--intake-sticky-top', `${nav.offsetHeight}px`);
    });
    observer.observe(nav);
    return () => observer.disconnect();
  }, [showJourneyChrome]);
  return (
    <Stack
      ref={rootRef}
      spacing={showJourneyChrome ? 3 : 0}
      sx={
        // Full width on purpose (GDN-3738): the two-column setup rail and the intake
        // form's own rail plus canvas need the room a 1200px cap took away.
        showJourneyChrome ? { p: { xs: 2, md: 4 }, color: wf.ink } : { display: 'contents' }
      }
    >
      {showJourneyChrome && (
        <Stack spacing={1}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 0.5, md: 2 }}
            sx={{ alignItems: { md: 'baseline' } }}
          >
            <Typography variant="h4" component="h1">
              Get billing-ready
            </Typography>
            <Typography color="text.secondary">
              Five steps, then your first claim. Patient import can run alongside the others.
            </Typography>
          </Stack>
          {toolbar}
        </Stack>
      )}
      {showJourneyChrome && (
        <Box
          component="nav"
          ref={navRef}
          aria-label="Practice setup stages"
          sx={{
            // Stacked stage cards can fill a small viewport and cover the form.
            // Only keep the single-row desktop navigation sticky.
            position: { xs: 'static', md: 'sticky' },
            top: 0,
            zIndex: 2,
            bgcolor: wf.surface,
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(5, minmax(0, 1fr))',
            },
            gap: { xs: 1, md: 0 },
            borderBottom: { md: `1px solid ${wf.line}` },
          }}
        >
          {intakeJourneyStages.map((stage, index) => {
            const selected = stage === selectedStage;
            const { statusLabel, caption, completed = false, tone, lockedReason } = stages[stage];
            const locked = Boolean(lockedReason);
            const chipTone: ChipTone = completed
              ? 'ok'
              : locked
                ? 'mut'
                : (tone ?? (selected ? 'info' : 'mut'));
            const last = index === intakeJourneyStages.length - 1;
            return (
              <ButtonBase
                key={stage}
                id={`${id}-${stage}-button`}
                aria-current={selected ? 'step' : undefined}
                aria-controls={`${id}-${stage}-panel`}
                aria-describedby={`${id}-${stage}-status`}
                aria-disabled={locked}
                onClick={() => {
                  if (!locked) onNavigate(stage);
                }}
                sx={{
                  ...cardSx,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  textAlign: 'left',
                  gap: 0.5,
                  border: { xs: `1px solid ${selected ? wf.blue : wf.line}`, md: 0 },
                  borderRadius: { xs: '8px', md: 0 },
                  borderBottom: { md: `3px solid ${selected ? wf.blue : 'transparent'}` },
                  bgcolor: { xs: selected ? wf.blueSoft : wf.surface, md: 'transparent' },
                  color: locked ? wf.muted : wf.ink,
                  '&.Mui-focusVisible': { outline: `3px solid ${wf.blue}`, outlineOffset: 2 },
                }}
              >
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
                  <StepMarker
                    index={index}
                    completed={completed}
                    locked={locked}
                    selected={selected}
                  />
                  <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.3, flex: 1 }}>
                    {labels[stage]}
                  </Typography>
                  {!last && (
                    <ChevronRightOutlinedIcon
                      aria-hidden="true"
                      sx={{ fontSize: 18, color: wf.faint, display: { xs: 'none', md: 'block' } }}
                    />
                  )}
                </Stack>
                <Box sx={{ pl: '38px' }}>
                  <Box component="span" sx={chipSx(chipTone)}>
                    {locked ? 'Locked' : statusLabel}
                  </Box>
                  <Typography
                    id={`${id}-${stage}-status`}
                    variant="caption"
                    component="p"
                    sx={{ color: wf.muted, mt: 1, lineHeight: 1.4 }}
                  >
                    {lockedReason ?? caption ?? statusLabel}
                  </Typography>
                </Box>
              </ButtonBase>
            );
          })}
        </Box>
      )}
      {intakeJourneyStages.map(stage => {
        const live = liveStages.includes(stage);
        return (
          <Box
            key={stage}
            component="section"
            id={`${id}-${stage}-panel`}
            aria-labelledby={showJourneyChrome ? `${id}-${stage}-button` : undefined}
            hidden={showJourneyChrome ? stage !== selectedStage : !live}
            sx={{
              minWidth: 0,
              ...(!showJourneyChrome && live ? { display: 'contents' } : {}),
            }}
          >
            {!showJourneyChrome ? (
              live ? (
                content[stage]
              ) : null
            ) : stages[stage].lockedReason ? (
              <Box sx={cardSx}>
                <Typography>{stages[stage].lockedReason}</Typography>
              </Box>
            ) : (
              content[stage]
            )}
          </Box>
        );
      })}
      {showJourneyChrome && kickoff}
    </Stack>
  );
}
